// src/pages/admin/AnnouncementsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, ToggleLeft, ToggleRight } from 'lucide-react'
import { adminAnnouncementsApi } from '../../api/services'
import { Card, CardBody, Button, StatusBadge, PageSpinner, Empty } from '../../components/common/ui'
import toast from 'react-hot-toast'

const INIT = { message:'', type:'info', is_active:true, starts_at:'', ends_at:'' }

export default function AnnouncementsPage() {
  const qc = useQueryClient()
  const [form, setForm] = useState(INIT)
  const [showForm, setShowForm] = useState(false)
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  const { data, isLoading } = useQuery({ queryKey: ['admin-announcements'], queryFn: () => adminAnnouncementsApi.list() })
  const create = useMutation({ mutationFn: () => adminAnnouncementsApi.store(form), onSuccess: () => { toast.success('Created'); qc.invalidateQueries(['admin-announcements']); setShowForm(false); setForm(INIT) } })
  const toggle = useMutation({ mutationFn: id => adminAnnouncementsApi.toggle(id), onSuccess: () => qc.invalidateQueries(['admin-announcements']) })
  const del = useMutation({ mutationFn: id => adminAnnouncementsApi.destroy(id), onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries(['admin-announcements']) } })

  if (isLoading) return <PageSpinner />
  const announcements = data?.data?.data || []

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold">Announcements</h1>
        <Button size="sm" onClick={() => setShowForm(!showForm)}><Plus size={14} /> New</Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardBody className="space-y-3">
            <textarea className="input w-full h-20" placeholder="Message *" value={form.message} onChange={set('message')} />
            <div className="flex gap-3">
              <select className="input" value={form.type} onChange={set('type')}>
                {['info','warning','success','error'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input className="input flex-1" type="datetime-local" placeholder="Starts" value={form.starts_at} onChange={set('starts_at')} />
              <input className="input flex-1" type="datetime-local" placeholder="Ends" value={form.ends_at} onChange={set('ends_at')} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.is_active} onChange={set('is_active')} /> Active immediately</label>
            <div className="flex gap-2">
              <Button loading={create.isPending} onClick={() => create.mutate()} disabled={!form.message.trim()}>Create</Button>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardBody>
        </Card>
      )}

      {announcements.length === 0 && !showForm && <Empty icon="📣" title="No announcements" />}

      <div className="space-y-2">
        {announcements.map(a => (
          <Card key={a.id}>
            <CardBody className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1"><span className={`text-xs px-2 py-0.5 rounded-full font-bold ${a.type === 'warning' ? 'bg-yellow-50 text-yellow-800' : a.type === 'error' ? 'bg-red-50 text-red-700' : a.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-blue-50 text-blue-800'}`}>{a.type}</span><StatusBadge status={a.is_active ? 'active' : 'draft'} /></div>
                <p className="text-sm text-gray-700 truncate">{a.message}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle.mutate(a.id)} className="p-1.5 rounded-lg hover:bg-surface">{a.is_active ? <ToggleRight size={18} className="text-green-500" /> : <ToggleLeft size={18} className="text-muted" />}</button>
                <button onClick={() => del.mutate(a.id)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={14} className="text-red-400" /></button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  )
}
